import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import ProductView from './pages/ProductView';
import CloudRunDetail from './pages/CloudRunDetail';
import GcpProjects from './pages/GcpProjects';
import MongoObservability from './pages/MongoObservability';
import MongoConfiguration from './pages/MongoConfiguration';
import VMInstances from './pages/VMInstances';
import CloudRunServices from './pages/CloudRunServices';
import CloudBuild from './pages/CloudBuild';
import CloudScheduler from './pages/CloudScheduler';
import CloudRunJobs from './pages/CloudRunJobs';
import CloudTasks from './pages/CloudTasks';
import VpnDetails from './pages/VpnDetails';
import GcpAudit from './pages/GcpReport';
import AddProductModal from './components/AddProductModal';
import AddTeamMemberModal from './components/AddTeamMemberModal';
import TeamMembers from './pages/TeamMembers';
import TeamMemberDetail from './pages/TeamMemberDetail';
import Search from './pages/Search';
import CostEstimation from './pages/CostEstimation';
import GpuInfo from './pages/GpuInfo';
import CloudLogs from './pages/CloudLogs';
import Login from './pages/Login';
import { useAuth } from './context/AuthContext';
import { getProducts, getGcpProjects, getTeamMembers } from './services/api';
import './App.css';

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<PrivateApp />} />
    </Routes>
  );
}

function PrivateApp() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="auth-boot">Loading…</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <MainApp />;
}

function MainApp() {
  const [products, setProducts] = useState([]);
  const [gcpProjects, setGcpProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    try { return localStorage.getItem('cm-selected-project') || 'all'; } catch { return 'all'; }
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('cm-sidebar-collapsed') === 'true'; } catch { return false; }
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try { localStorage.setItem('cm-selected-project', selectedProjectId); } catch { /* ignore */ }
  }, [selectedProjectId]);

  useEffect(() => {
    try { localStorage.setItem('cm-sidebar-collapsed', String(sidebarCollapsed)); } catch { /* ignore */ }
  }, [sidebarCollapsed]);

  const fetchProducts = async () => {
    try {
      const res = await getProducts();
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchGcpProjects = async () => {
    try {
      const res = await getGcpProjects();
      setGcpProjects(res.data);
    } catch (err) {
      console.error('Failed to fetch GCP projects:', err);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await getTeamMembers();
      setTeamMembers(res.data);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchGcpProjects();
    fetchTeamMembers();
  }, []);

  const filteredProducts = selectedProjectId === 'all'
    ? products
    : products.filter((p) =>
        p.cloudRuns?.some((cr) => cr.gcpProjectId === selectedProjectId)
      );

  const handleProductCreated = (product) => {
    fetchProducts();
    setShowAddProduct(false);
    navigate(`/products/${product._id}`);
  };

  const handleTeamMemberCreated = (member) => {
    fetchTeamMembers();
    setShowAddTeamMember(false);
    navigate(`/team/${member._id}`);
  };

  return (
    <div className="app-layout">
      <Sidebar
        products={filteredProducts}
        gcpProjects={gcpProjects}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        onAddProduct={() => setShowAddProduct(true)}
        currentPath={location.pathname}
      />
      <main className={`main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
        {gcpProjects.length > 0 && location.pathname !== '/gcp-projects' && !location.pathname.startsWith('/team') && location.pathname !== '/search' && location.pathname !== '/cost-estimation' && location.pathname !== '/gpu-info' && location.pathname !== '/logs' && location.pathname !== '/mongo-configuration' && (
          <ProjectSelectorBar
            gcpProjects={gcpProjects}
            products={products}
            selectedProjectId={selectedProjectId}
            onSelectProject={(projectId) => {
              setSelectedProjectId(projectId);
              const isOnProduct = location.pathname.startsWith('/products/');
              if (isOnProduct) {
                const filtered = projectId === 'all'
                  ? products
                  : products.filter((p) => p.cloudRuns?.some((cr) => cr.gcpProjectId === projectId));
                if (filtered.length > 0) {
                  navigate(`/products/${filtered[0]._id}`);
                } else {
                  navigate('/');
                }
              }
            }}
          />
        )}
        <Routes>
          <Route path="/" element={<Dashboard products={filteredProducts} gcpProjects={gcpProjects} />} />
          <Route
            path="/products/:id"
            element={<ProductViewWrapper onProductUpdated={fetchProducts} gcpProjects={gcpProjects} />}
          />
          <Route path="/cloud-run/:projectId/:region/:serviceName" element={<CloudRunDetail />} />
          <Route
            path="/gcp-projects"
            element={<GcpProjects onProjectsChanged={() => { fetchGcpProjects(); fetchProducts(); }} products={products} />}
          />
          <Route
            path="/mongo-observability"
            element={<MongoObservability gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route path="/mongo-configuration" element={<MongoConfiguration />} />
          <Route
            path="/vm-instances"
            element={<VMInstances gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route
            path="/cloud-run-services"
            element={<CloudRunServices gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route
            path="/cloud-build"
            element={<CloudBuild gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route
            path="/cloud-scheduler"
            element={<CloudScheduler gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route
            path="/cloud-run-jobs"
            element={<CloudRunJobs gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route
            path="/cloud-tasks"
            element={<CloudTasks gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route
            path="/vpn-details"
            element={<VpnDetails gcpProjects={gcpProjects} selectedProjectId={selectedProjectId} />}
          />
          <Route
            path="/gcp-audit"
            element={<GcpAudit gcpProjects={gcpProjects} />}
          />
          <Route
            path="/team"
            element={
              <TeamMembers
                teamMembers={teamMembers}
                onAddMember={() => setShowAddTeamMember(true)}
                onRefresh={fetchTeamMembers}
              />
            }
          />
          <Route
            path="/team/:id"
            element={<TeamMemberDetail gcpProjects={gcpProjects} onMemberUpdated={fetchTeamMembers} />}
          />
          <Route
            path="/search"
            element={<Search gcpProjects={gcpProjects} products={products} teamMembers={teamMembers} />}
          />
          <Route
            path="/cost-estimation"
            element={<CostEstimation gcpProjects={gcpProjects} products={products} />}
          />
          <Route path="/gpu-info" element={<GpuInfo />} />
          <Route path="/logs" element={<CloudLogs gcpProjects={gcpProjects} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {showAddProduct && (
        <AddProductModal
          gcpProjects={gcpProjects}
          onClose={() => setShowAddProduct(false)}
          onCreated={handleProductCreated}
        />
      )}

      {showAddTeamMember && (
        <AddTeamMemberModal
          gcpProjects={gcpProjects}
          onClose={() => setShowAddTeamMember(false)}
          onCreated={handleTeamMemberCreated}
        />
      )}
    </div>
  );
}

function ProjectSelectorBar({ gcpProjects, products, selectedProjectId, onSelectProject }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabel = selectedProjectId === 'all'
    ? 'All Projects'
    : gcpProjects.find((p) => p._id === selectedProjectId)?.projectName || 'Select Project';

  return (
    <div className="project-bar" ref={ref}>
      <button className="project-bar-btn" onClick={() => setOpen(!open)}>
        <span className="project-bar-label">{selectedLabel}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className="project-bar-dropdown">
          <div
            className={`project-bar-option ${selectedProjectId === 'all' ? 'active' : ''}`}
            onClick={() => { onSelectProject('all'); setOpen(false); }}
          >
            All Projects
            <span className="project-bar-count">{products.length}</span>
          </div>
          {gcpProjects.map((gp) => {
            const count = products.filter((p) =>
              p.cloudRuns?.some((cr) => cr.gcpProjectId === gp._id)
            ).length;
            return (
              <div
                key={gp._id}
                className={`project-bar-option ${selectedProjectId === gp._id ? 'active' : ''}`}
                onClick={() => { onSelectProject(gp._id); setOpen(false); }}
              >
                <span className="project-bar-option-name">{gp.projectName}</span>
                <span className="project-bar-count">{count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProductViewWrapper(props) {
  const { id } = useParams();
  return <ProductView key={id} {...props} />;
}
